import { createSlice, current } from '@reduxjs/toolkit';
import { LOADING_STATUS } from '@sonifi/wb-core/common/Constants';
import { getTimelineDuration } from '@sonifi/wb-happenings/services/happeningsHelper';

const initialState = {
  clipId: -1,
  hasChanges: false,
  isEditingSlice: false,
  isPublishing: false,
  loading: LOADING_STATUS.loading,
  loadedGroupId: null,
  loadingTimeline: LOADING_STATUS.loading,
  multiSelect: false,
  publishStatus: null,
  selectAll: false,
  timelineDuration: {
    value: 0,
    isApproximate: false,
  },
  timelineDurationLimit: null,
  timelineList: [],
  timelineListWithOptGroups: {},
  timelineSelected: null,
};

export const happeningsSlice = createSlice({
  name: 'happenings',
  initialState,
  reducers: {
    deleteTimelineClip(state, action) {
      const { multiSelect, selectAll, timelineSelected } = current(state);
      const clipSelected = action.payload;
      const playlist = clipSelected
        ? timelineSelected.playlist.filter(
            (clip) => clip.clipId !== clipSelected.clipId,
          )
        : timelineSelected.playlist.filter((clip) => !clip.itemSelected);
      const newTimelineSelected = {
        ...timelineSelected,
        playlist,
      };
      state.timelineSelected = newTimelineSelected;
      state.timelineDuration = getTimelineDuration(newTimelineSelected);
      state.multiSelect = selectAll ? false : multiSelect;
      state.selectAll = false;
      state.hasChanges = true;
    },
    deleteTimelineAudio(state) {
      const { timelineSelected } = current(state);
      state.timelineSelected = {
        ...timelineSelected,
        audio: null,
      };
      state.hasChanges = true;
    },
    selectClip(state, action) {
      const { clipSelected, checked } = action.payload;
      const { timelineSelected } = current(state);
      const playlist = timelineSelected.playlist.map((clip) =>
        clip.clipId === clipSelected.clipId
          ? { ...clipSelected, itemSelected: checked }
          : clip,
      );
      state.timelineSelected = {
        ...timelineSelected,
        playlist,
      };
      state.selectAll = playlist.every((clip) => clip.itemSelected);
    },
    setClipDuration(state, action) {
      const { clip, duration } = action.payload;
      const { timelineSelected } = current(state);

      const playlist = timelineSelected.playlist.map((cl) =>
        clip.clipId === cl.clipId
          ? { ...cl, duration, useDefaultDuration: false }
          : cl,
      );
      const newTimelineSelected = {
        ...timelineSelected,
        playlist,
      };
      state.timelineSelected = newTimelineSelected;
      state.timelineDuration = getTimelineDuration(newTimelineSelected);

      state.hasChanges = true;
    },
    setClipDate(state, action) {
      const { clip, endDate, startDate } = action.payload;
      const { timelineSelected } = current(state);
      const playlist = timelineSelected.playlist.map((cl) => {
        if (clip.clipId === cl.clipId) {
          if (startDate) {
            return {
              ...cl,
              active: { ...cl.active, start_date: startDate },
              media: {
                ...cl.media,
                active: { ...cl.media.active, start_date: startDate },
              },
            };
          }
          if (endDate) {
            return {
              ...cl,
              active: { ...cl.active, end_date: endDate },
              media: {
                ...cl.media,
                active: { ...cl.media.active, end_date: endDate },
              },
            };
          }
        }
        return cl;
      });
      state.timelineSelected = {
        ...timelineSelected,
        playlist,
      };
      state.hasChanges = true;
    },
    setClipId(state, action) {
      state.clipId = action.payload;
    },
    checkHasClipInvalid(state, action) {
      const { mediaItems } = action.payload;
      const { timelineSelected } = current(state);

      let playlist = timelineSelected?.playlist ?? [];
      let audioUpdated = {};
      for (const mediaItem of mediaItems) {
        playlist = playlist.map((cl) => ({
          ...cl,
          isInvalid: cl.media.id === mediaItem.data.id ? true : cl.isInvalid,
        }));
        audioUpdated = {
          ...timelineSelected.audio,
          isZombie: mediaItem.data.id === timelineSelected.audio?.id,
        };

        if (audioUpdated?.id) {
          audioUpdated = {
            ...timelineSelected.audio,
            isZombie: mediaItem.data.id === timelineSelected.audio?.id,
          };
        }
      }

      state.timelineSelected = {
        ...timelineSelected,
        playlist,
        audio: audioUpdated?.id ? audioUpdated : null,
      };
    },
    setAudio(state, action) {
      const audio = action.payload;
      const { timelineSelected } = current(state);
      state.timelineSelected = {
        ...timelineSelected,
        audio,
      };
      state.hasChanges = true;
    },
    setHappeningsLoading(state, action) {
      state.loading = action.payload;
    },
    setLoadedGroupId(state, action) {
      state.loadedGroupId = action.payload;
    },
    setIsPublishing(state, action) {
      state.isPublishing = action.payload;
    },
    setPublishStatus(state, action) {
      state.publishStatus = action.payload;
    },
    setTimelineDuration(state, action) {
      const { timelineSelected } = current(state);
      const newTimelineSelected = {
        ...timelineSelected,
        default_duration: action.payload,
      };
      const timelineDuration = getTimelineDuration(newTimelineSelected);
      state.timelineSelected = newTimelineSelected;
      state.timelineDuration = timelineDuration;
      state.hasChanges = true;
    },
    setTimelineMultiSelect(state, action) {
      state.multiSelect = action.payload;
    },
    addTimelineClips(state, action) {
      const { clips, fromClip } = action.payload;
      const { timelineSelected } = current(state);
      const playlist = [];
      const clipsIndexStart = timelineSelected.playlist.findIndex(
        (cl) => cl.clipId === fromClip?.clipId,
      );
      timelineSelected.playlist.forEach((cl, index) => {
        if (clipsIndexStart === index) {
          playlist.push(...clips);
        }
        playlist.push(cl);
      });
      if (!fromClip) {
        playlist.push(...clips);
      }

      const finalPlaylist = playlist.map((cl, index) => ({
        ...cl,
        order: index,
      }));

      const newTimelineSelected = {
        ...timelineSelected,
        playlist: finalPlaylist,
      };
      const timelineDuration = getTimelineDuration(newTimelineSelected);
      state.timelineDuration = timelineDuration;
      state.timelineSelected = newTimelineSelected;
      state.hasChanges = true;
    },
    swapTimelineClips(state, action) {
      const { clipOrigin, clipDestiny } = action.payload;
      const { timelineSelected } = current(state);
      const playlist = timelineSelected.playlist.map((clip) => {
        if (clip.clipId === clipOrigin.clipId) {
          return clipDestiny;
        }
        if (clip.clipId === clipDestiny.clipId) {
          return clipOrigin;
        }
        return clip;
      });
      state.timelineSelected = {
        ...timelineSelected,
        playlist,
      };
      state.hasChanges = true;
    },
    setTimelineList(state, action) {
      const { timelineList, timelineListWithOptGroups } = action.payload;
      state.timelineList = timelineList;
      state.timelineListWithOptGroups = timelineListWithOptGroups;
    },
    setTimeline(state, action) {
      const { timelineDuration, timelineDurationLimit, timelineSelected } =
        action.payload;
      state.timelineSelected = timelineSelected;
      state.timelineDuration = timelineDuration;
      if (timelineDurationLimit) {
        state.timelineDurationLimit = timelineDurationLimit;
      }
    },
    toggleTimeLineSelectAll(state, action) {
      const selectAll = action.payload;
      const { timelineSelected } = current(state);
      const playlist = timelineSelected.playlist.map((clip) => ({
        ...clip,
        itemSelected: selectAll,
      }));
      state.timelineSelected = {
        ...timelineSelected,
        playlist,
      };
      state.selectAll = selectAll;
    },
    toggleDropdownStatus(state, action) {
      const clip = action.payload;
      const { multiSelect, timelineSelected } = current(state);
      let playlist = [...timelineSelected.playlist];
      if (multiSelect) {
        playlist = playlist
          .filter((cp) => cp.itemSelected)
          .map((cp) => ({
            ...cp,
            showDropdown: !cp.showDropdown,
          }));
      } else {
        const clipIndex = playlist.findIndex((cp) => cp.clipId === clip.clipId);
        playlist[clipIndex] = {
          ...clip,
          showDropdown: !clip.showDropdown,
        };
      }
      state.timelineSelected = {
        ...timelineSelected,
        playlist,
      };
    },
    setHasChanges(state, action) {
      state.hasChanges = action.payload;
    },
    setLoadingTimeline(state, action) {
      state.loadingTimeline = action.payload;
    },
    updateClipSlide(state, action) {
      const { slide } = action.payload;
      const { timelineSelected } = current(state);
      const playlist = timelineSelected.playlist.map((cp) => {
        if (cp.media.id === slide.id && cp.media.type === slide.type) {
          return { ...cp, media: slide };
        }
        return cp;
      });

      state.timelineSelected = {
        ...timelineSelected,
        playlist,
      };
    },
    setIsEditingSlice(state, action) {
      state.isEditingSlice = action.payload;
    },
    cleanHappeningsSlice() {
      return initialState;
    },
    addDeeplink(state, action) {
      const deeplink = action.payload.deeplink;
      const { timelineSelected } = current(state);
      state.timelineSelected = {
        ...timelineSelected,
        deeplink,
      };
    },
  },
});

export const {
  addDeeplink,
  addTimelineClips,
  checkHasClipInvalid,
  cleanHappeningsSlice,
  deleteTimelineClip,
  deleteTimelineAudio,
  selectClip,
  setAudio,
  setClipDate,
  setClipDuration,
  setClipId,
  setHappeningsLoading,
  setHasChanges,
  setIsEditingSlice,
  setIsPublishing,
  setLoadedGroupId,
  setLoadingTimeline,
  setPublishStatus,
  setTimeline,
  setTimelineDuration,
  setTimelineList,
  setTimelineMultiSelect,
  swapTimelineClips,
  toggleDropdownStatus,
  toggleTimeLineSelectAll,
  updateClipSlide,
} = happeningsSlice.actions;

export default happeningsSlice.reducer;
